import { useMutation } from "@tanstack/react-query";
import { useAxios } from "auth-sdk";
import { addError } from "@wayflyer/blackbox-fe";
import { WayflyerSchemas } from "api-types";
import type { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { queryClient } from "shared-utils";

import { useErrorModal } from "../../error-handling/app-error-modal-provider";
import { ENDPOINTS } from "../common/constants";

export const useEditCompanyMemberMutation = (customerDomain: string, companyId: string, userId: string) => {
  const client = useAxios();
  const navigate = useNavigate();
  const mutationUrl = ENDPOINTS.EDIT_COMPANY_MEMBER.replace(":companyId", companyId).replace(":userId", userId);

  return useMutation({
    mutationKey: ["editCompanyMember", companyId, userId],
    mutationFn: (data: WayflyerSchemas.EditCompanyMemberParams) => {
      return client.patch(mutationUrl, data);
    },
    onSuccess: () => {
      navigate(`/${customerDomain}/settings?tab=team`);
    },
    onError: (error: AxiosError<{ message: string }>) => {
      addError(error);
    },
  });
};

type SharedMutationOptions = {
  method: "delete" | "post";
  endpoint: string;
  mutationKeyPrefix: string;
  queryKeyToInvalidate: string[];
};

export const useDeleteCompanyMemberMutation = (
  companyId: string,
  onSuccess: () => void,
  onClose: () => void,
  userId: string | null
) =>
  useSharedCompanyMutation(companyId, onSuccess, onClose, userId, {
    method: "delete",
    endpoint: ENDPOINTS.DELETE_COMPANY_MEMBER.replace(":userId", ":objectId"),
    mutationKeyPrefix: "deleteCompanyInvite",
    queryKeyToInvalidate: ["company-members", companyId],
  });

export const useDeleteCompanyInviteMutation = (
  companyId: string,
  onSuccess: () => void,
  onClose: () => void,
  inviteId: number | null
) =>
  useSharedCompanyMutation(companyId, onSuccess, onClose, inviteId?.toString() ?? null, {
    method: "delete",
    endpoint: ENDPOINTS.DELETE_COMPANY_INVITE.replace(":inviteId", ":objectId"),
    mutationKeyPrefix: "deleteCompanyInvite",
    queryKeyToInvalidate: ["company-invites", companyId],
  });

export const useResendInviteMutation = (
  companyId: string,
  onSuccess: () => void,
  onClose: () => void,
  inviteId: number | null
) =>
  useSharedCompanyMutation(companyId, onSuccess, onClose, inviteId?.toString() ?? null, {
    method: "post",
    endpoint: ENDPOINTS.RESEND_COMPANY_INVITE.replace(":inviteId", ":objectId"),
    mutationKeyPrefix: "resendCompanyInvite",
    queryKeyToInvalidate: ["company-invites", companyId],
  });

const useSharedCompanyMutation = (
  companyId: string,
  onSuccess: () => void,
  onClose: () => void,
  objectId: string | null,
  options: SharedMutationOptions
) => {
  const client = useAxios();
  const { openErrorModal, closeErrorModal } = useErrorModal();

  return useMutation({
    mutationKey: [options.mutationKeyPrefix, companyId, objectId],
    mutationFn: (objectId: string) => {
      const mutationUrl = options.endpoint.replace(":companyId", companyId).replace(":objectId", objectId);
      return client[options.method](mutationUrl);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: options.queryKeyToInvalidate });
      onSuccess();
    },
    onError: (error: AxiosError<{ message: string }>) => {
      openErrorModal({
        variant: "submit-error",
        error: new Error(`Something went wrong: ${error.message}`),
        secondaryAction: {
          content: "Close",
          onAction: () => {
            closeErrorModal();
            onClose();
          },
        },
      });
    },
  });
};
